import { Component, inject, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ErrorHandlerService } from '../../../../../_shared/services/error-handler.service';
import { ExecutiveStatsService } from '../../../services/executive-stats.service';
import { DateInterval } from '../../../../../_shared/enums';
import { GroupedChartData, TimeSeriesGroupingHelper } from '../../../../../_shared/helpers/chart-grouping/time-series-grouping.helper';
import { EmptyStateComponent } from '../../../../../_shared/components/states/empty-state/empty-state.component';
import { Subject } from 'rxjs';
import { GraphTrendIconComponent } from '../../../../../_shared/components/graphs/graph-trend-icon/graph-trend-icon.component';
import { AccountFilterComponent } from '../../../../../_shared/components/filters/account-filter/account-filter.component';
import { DateFilterComponent } from '../../../../../_shared/components/filters/date-filter/date-filter.component';
import { AccountFilterData, DateIntervalFilterData, DriverFilterData } from '../../../../../_shared/models';
import { isEmptyObject } from '../../../../../_shared/helpers/object.helper';
import { GroupedBarChartComponent } from '../../../../../_shared/components/graphs/grouped-bar-chart/grouped-bar-chart.component';
import { ExecutiveStatsRequest, ExecutiveStatsSummaryRequest } from '../../../../../_shared/models/driver-summary-request.model';
import { GraphErrorStateComponent } from '../../../../../_shared/components/graphs/graph-error-state/graph-error-state.component';
import { differenceInDays } from 'date-fns';
import { calculateChartYTicks } from '../../../../../_shared/helpers/chart-y-ticks.helper';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-trips-modal',
  standalone: true,
  imports: [GroupedBarChartComponent, EmptyStateComponent, GraphTrendIconComponent, AccountFilterComponent, DateFilterComponent, GraphErrorStateComponent, TranslocoDirective, TranslocoPipe],
  templateUrl: './trips-modal.component.html',
  styleUrl: './trips-modal.component.scss'
})
export class TripsModalComponent implements OnInit {
  private activeModal = inject(NgbActiveModal);
  private executiveStatsService = inject(ExecutiveStatsService);
  private errorHandlerService = inject(ErrorHandlerService);
  private transloco = inject(TranslocoService);

  chartData: GroupedChartData[] = [];
  readonly chartHeight = 335;
  private readonly defaultYTicks = [100, 80, 60, 40, 20, 0];
  yTicks: number[] = this.defaultYTicks;

  readonly allSubgroups = [
    { name: 'Poor', legend: this.transloco.translate('executiveStats.chart.poor'), color: '#FF7F00' },
    { name: 'Fair', legend: this.transloco.translate('executiveStats.chart.fair'), color: '#F4BC2B' },
    { name: 'Good', legend: this.transloco.translate('executiveStats.chart.good'), color: '#50AD41' },
    { name: 'Globaltrends', legend: this.transloco.translate('executiveStats.chart.globalTrends'), color: '#3873C9' }
  ];

  // Dynamic subgroups based on which have data
  subgroups: Array<{ name: string, legend: string, color: string }> = [];

  public thresholds = {
    day: { floor: 2, ceiling: 4 },
    weekly: { floor: 10, ceiling: 20 },
    monthly: { floor: 40, ceiling: 80 }
  };

  isLoading: boolean = true;
  hasError: boolean = false;
  accountFilter: AccountFilterData;
  dateFilter: DateIntervalFilterData;
  driverFilter: DriverFilterData;
  totalValue: number = 0;
  percentageChange: number = 0;
  private cancelSubject: Subject<void> = new Subject<void>();

  constructor() {
    this.accountFilter = {} as AccountFilterData;
    this.dateFilter = {} as DateIntervalFilterData;
    this.driverFilter = {} as DriverFilterData;
  }

  async ngOnInit(): Promise<void> {
    if (!isEmptyObject(this.dateFilter) && !isEmptyObject(this.accountFilter)) {
      await this.loadData();
    }
  }

  async onAccountFilterChange(filter: AccountFilterData): Promise<void> {
    this.accountFilter = filter;
    this.updateFilter();
  }

  async onDateFilterChange(filter: DateIntervalFilterData): Promise<void> {
    this.dateFilter = filter;
    this.updateFilter();
  }

  async updateFilter(): Promise<void> {
    if (!isEmptyObject(this.dateFilter) && !isEmptyObject(this.accountFilter)) {
      await this.loadData();
    }
  }

  async loadData(): Promise<void> {
    this.isLoading = true;
    this.hasError = false;

    try {
      const summaryRequest: ExecutiveStatsSummaryRequest = {
        driverIds: this.driverFilter.driverIds ?? [],
        accountIds: this.accountFilter.accountIds ?? [],
        timeFrom: this.dateFilter.startDate!,
        timeTo: this.dateFilter.endDate!,
        previousTimeFrom: this.dateFilter.previousCycleStartDate!,
        previousTimeTo: this.dateFilter.previousCycleEndDate!
      };

      const request: ExecutiveStatsRequest = {
        driverIds: this.driverFilter.driverIds ?? [],
        accountIds: this.accountFilter.accountIds ?? [],
        timeFrom: this.dateFilter.startDate!,
        timeTo: this.dateFilter.endDate!,
        interval: this.dateFilter.interval
      };

      this.cancelSubject.next();
      const [trips, tripInsights, globalTrips] = await Promise.all([
        this.executiveStatsService.getTrips(request, this.cancelSubject),
        this.executiveStatsService.getTripInsightsSummary(summaryRequest, this.cancelSubject),
        this.executiveStatsService.getGlobalTrips(request, this.cancelSubject)
      ]);

      const tripsData = trips.data ?? [];
      const globalTripsData = globalTrips.data ?? [];

      this.totalValue = tripInsights.data?.totalValue ?? 0;
      this.percentageChange = tripInsights.data?.percentageDifference ?? 0;
      this.chartData = this.processResponses(tripsData, globalTripsData);
      this.yTicks = this.calculateYTicks(this.chartData);

      // Update subgroups to only include those with data
      this.updateActiveSubgroups();

      this.isLoading = false;
    } catch (error) {
      this.errorHandlerService.handleError(error, {
        callback: () => {
          this.hasError = true;
          this.isLoading = false;
          this.chartData = [];
        }
      });
    }
  }

  private getThresholdForInterval(): { floor: number; ceiling: number } {
    if (this.dateFilter.interval === DateInterval.Week) {
      return this.thresholds.weekly;
    } else if (this.dateFilter.interval === DateInterval.Day || this.dateFilter.isCustomDate) {
      return this.thresholds.day;
    }
    return this.thresholds.monthly;
  }

  private processResponses(tripsData: any[], globalTripsData: any[]): GroupedChartData[] {
    const daysDifference = differenceInDays(this.dateFilter.endDate!, this.dateFilter.startDate!);
    const groupingType = this.determineGroupingType(daysDifference, this.dateFilter.interval);

    // First group the data by time period
    const datasets = [
      { data: tripsData, label: 'You' },
      { data: globalTripsData, label: 'Globaltrends' }
    ];

    let groupedData: GroupedChartData[];
    switch (groupingType) {
      case 'hourly':
        groupedData = TimeSeriesGroupingHelper.groupByHours(datasets);
        break;
      case 'daily':
        groupedData = TimeSeriesGroupingHelper.groupByDays(datasets, this.dateFilter.startDate!, this.dateFilter.endDate!);
        break;
      case 'monthly':
        groupedData = TimeSeriesGroupingHelper.groupByMonths(datasets, this.dateFilter.startDate!, this.dateFilter.endDate!);
        break;
      default:
        groupedData = TimeSeriesGroupingHelper.groupByWeeks(datasets, this.dateFilter.startDate!, this.dateFilter.endDate!);
        break;
    }

    // Apply threshold-based coloring
    const threshold = this.getThresholdForInterval();
    return groupedData.map(item => {
      const youVal = Number(item['You']) || 0;
      const globalVal = Number(item['Globaltrends']) || 0;

      return {
        ...item,
        Poor: youVal < threshold.floor ? youVal : 0,
        Fair: youVal >= threshold.floor && youVal <= threshold.ceiling ? youVal : 0,
        Good: youVal > threshold.ceiling ? youVal : 0,
        Globaltrends: globalVal
      } as GroupedChartData;
    });
  }

  private determineGroupingType(daysDifference: number, interval: DateInterval): 'hourly' | 'daily' | 'weekly' | 'monthly' {
    if (daysDifference === 0) return 'hourly';
    if (daysDifference <= 7) return 'daily';
    if (daysDifference > 60) return 'monthly';
    return 'weekly';
  }

  private calculateYTicks(chartData: GroupedChartData[]): number[] {
    if (!chartData?.length) return this.defaultYTicks;

    const allValues = chartData.flatMap(item => [
      Number(item['Poor']) || 0,
      Number(item['Fair']) || 0,
      Number(item['Good']) || 0,
      Number(item['Globaltrends']) || 0
    ]);

    return calculateChartYTicks(allValues, this.defaultYTicks, 6);
  }

  formatPercentChange(): string {
    const sign = this.percentageChange > 0 ? '+' : '';
    return `${sign}${this.percentageChange.toLocaleString('default', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}%`;
  }

  isUptrendPositive(): boolean {
    return true;
  }

  formatValue(): string {
    return `${this.totalValue.toLocaleString()}`;
  }

  hasData(): boolean {
    if (!this.chartData || this.chartData.length === 0) return false;
    return this.chartData.some(item => {
      return this.allSubgroups.some(sg => (Number(item[sg.name]) || 0) > 0);
    });
  }

  private updateActiveSubgroups(): void {
    const activeNames = new Set<string>();
    this.chartData.forEach(item => {
      this.allSubgroups.forEach(sg => {
        if ((Number(item[sg.name]) || 0) > 0) {
          activeNames.add(sg.name);
        }
      });
    });
    this.subgroups = this.allSubgroups.filter(sg => activeNames.has(sg.name));
  }

  close(): void {
    this.activeModal.close();
  }
}